import { useQuery } from '@tanstack/react-query'
import { useId } from 'react'

import { actionsQuery } from '@/api/actions'

import { describeRef, fxWord } from '../describe'
import { useEditor } from './context'
import { asObj, asStrings, clone, insertAt, moveItem, removeAt, setOrDelete, splitStateRef, type Obj } from './doc'
import { AddButton, ComboInput, CommitInput, EditField, IssueLines, ItemCard, ListControls, nameNormalize, NumberInput, RefInput, SELECT_CLASS, SelectInput, TagInput, ValueInput, type Option } from './fields'
import { useListKeys } from './listKeys'

/**
 * The list parts of an action: the moves each participant uses (with fallbacks when a puppet does
 * not have one), effects (per step or automatic), what is true afterwards, and what to try instead
 * when the action can't happen.
 */

const words = (s: string): string => s.replace(/_/g, ' ')

/* -------------------------------------------------------------------------- */
/* Clips                                                                       */
/* -------------------------------------------------------------------------- */

/** `clips`: role → move name → fallback ladder (the first move the puppet has is played). */
export function ClipsEditor({ clips, onChange }: { clips: Obj; onChange(next: Obj): void }) {
  const { roles, meta, lists } = useEditor()
  const shown = [...roles, ...Object.keys(clips).filter((r) => !roles.includes(r))]
  return (
    <div className="flex flex-col gap-4">
      {shown.map((role) => {
        const table = asObj(clips[role])
        const entries = Object.entries(table)
        const setTable = (next: Obj): void => onChange(setOrDelete(clips, [role], next))
        return (
          <div key={role} className="flex flex-col gap-2" data-field-path={`clips.${role}`} data-testid="clips-role" data-role={role}>
            <p className="text-sm font-semibold text-ink">{words(role)}</p>
            <IssueLines path={`clips.${role}`} />
            {entries.length === 0 ? <p className="text-xs text-ink-3">Uses the moves named in the steps as they are.</p> : null}
            {entries.map(([name, ladder], i) => (
              <div key={`${i}`} className="grid gap-2 md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] md:items-start">
                <CommitInput
                  label={`Move ${i + 1} of the ${words(role)}`}
                  value={name}
                  suggestions={meta.clip_names}
                  normalize={nameNormalize}
                  accept={(key) => key === name || !(key in table)}
                  onCommit={(key) => setTable(Object.fromEntries(entries.map(([k, l]) => (k === name ? [key, l] : [k, l]))))}
                />
                <EditField path={`clips.${role}.${name}`} label={`If the puppet can’t “${words(name)}”, try`} hideLabel>
                  {(ctrl) => <TagInput ctrl={ctrl} ordered values={asStrings(ladder)} suggestions={meta.clip_names} listId={lists?.clips} onChange={(v) => setTable({ ...table, [name]: v })} placeholder="Then try… (in order)" />}
                </EditField>
                <ListControls
                  name={`the move “${words(name)}” of the ${words(role)}`}
                  index={i}
                  count={entries.length}
                  onRemove={() => {
                    const next = { ...table }
                    delete next[name]
                    setTable(next)
                  }}
                />
              </div>
            ))}
            <AddButton
              onClick={() => {
                let n = 1
                let key = 'move_1'
                while (key in table) key = `move_${++n}`
                setTable({ ...table, [key]: [] })
              }}
            >
              Add a move for the {words(role)}
            </AddButton>
          </div>
        )
      })}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Effects                                                                     */
/* -------------------------------------------------------------------------- */

/** A step's `fx` or the action's `fx_auto`: an effect, where, and when. */
export function FxList({ path, items, onChange, auto = false }: { path: string; items: unknown[]; onChange(next: unknown[]): void; auto?: boolean }) {
  const { meta, roles } = useEditor()
  const fxOpts: Option[] = meta.fx_kinds.map((k) => ({ value: k, label: fxWord(k) }))
  const onOpts = [...meta.clip_names, ...roles.flatMap((r) => meta.moments.map((e) => `${r}:${e}`))]
  return (
    <div className="flex flex-col gap-2">
      {items.map((raw, i) => {
        const fx = asObj(raw)
        const set = (key: string, v: unknown): void => onChange(items.map((x, j) => (j === i ? setOrDelete(fx, [key], v) : x)))
        return (
          <div key={i} className="grid gap-2 rounded-xl border border-line p-2 md:grid-cols-[10rem_minmax(0,1fr)_8rem_auto] md:items-start" data-field-path={`${path}.${i}`}>
            <EditField path={`${path}.${i}.kind`} label="Effect">
              {(ctrl) => <SelectInput ctrl={ctrl} value={typeof fx.kind === 'string' ? fx.kind : undefined} options={fxOpts} onChange={(v) => set('kind', v)} />}
            </EditField>
            <EditField path={`${path}.${i}.at`} label="Where">
              {(ctrl) => <RefInput ctrl={ctrl} value={fx.at} onChange={(v) => set('at', v)} />}
            </EditField>
            {auto ? (
              <EditField path={`${path}.${i}.on`} label="When" hint="Empty = at the end.">
                {(ctrl) => <ComboInput ctrl={ctrl} value={fx.on} suggestions={onOpts} onChange={(v) => set('on', v ?? null)} />}
              </EditField>
            ) : (
              <EditField path={`${path}.${i}.at_s`} label="After (s)" hint="Empty = at once.">
                {(ctrl) => <NumberInput ctrl={ctrl} value={fx.at_s} min={0} step={0.1} onChange={(v) => set('at_s', v ?? null)} />}
              </EditField>
            )}
            <div className="md:pt-6">
              <ListControls name={`effect ${i + 1}`} index={i} count={items.length} onRemove={() => onChange(removeAt(items, i))} />
            </div>
          </div>
        )
      })}
      <AddButton onClick={() => onChange([...items, { kind: 'sparkle', at: roles[0] ? `$${roles[0]}` : 'spot:middle' }])}>{auto ? 'Add an automatic effect' : 'Add an effect'}</AddButton>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Results                                                                     */
/* -------------------------------------------------------------------------- */

/** `result`: after it happens, `$role.key` becomes a value. */
export function ResultsEditor({ path, items, onChange }: { path: string; items: unknown[]; onChange(next: unknown[]): void }) {
  const { roles, meta, lists } = useEditor()
  const [keys, keyOps] = useListKeys(items.length)
  return (
    <div className="flex flex-col gap-2">
      {items.length === 0 ? <p className="text-sm text-ink-2">Nothing changes afterwards.</p> : null}
      {items.map((raw, i) => {
        const r = asObj(raw)
        const [role, key] = splitStateRef(r.set)
        const setRef = (nr: string, nk: string): void => onChange(items.map((x, j) => (j === i ? { ...r, set: `$${nr}.${nk}` } : x)))
        return (
          <div key={keys[i]} className="grid gap-2 rounded-xl border border-line p-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start" data-field-path={`${path}.${i}`} data-testid="result-row">
            <EditField path={`${path}.${i}.set`} label="Whose">
              {(ctrl) => <SelectInput ctrl={ctrl} value={role || undefined} options={roles.map((x) => ({ value: x, label: words(x) }))} onChange={(v) => setRef(v ?? '', key || 'mood')} />}
            </EditField>
            <EditField path={`${path}.${i}.set.key`} label="What">
              {(ctrl) => <ComboInput ctrl={ctrl} value={key} suggestions={meta.state_keys} listId={lists?.stateKeys} normalize={nameNormalize} onChange={(k) => setRef(role || roles[0] || 'actor', k ?? '')} />}
            </EditField>
            <EditField path={`${path}.${i}.to`} label="Becomes">
              {(ctrl) => <ValueInput ctrl={ctrl} value={r.to} stateKey={key} noneIsNull onChange={(v) => onChange(items.map((x, j) => (j === i ? { ...r, to: v } : x)))} />}
            </EditField>
            <div className="md:pt-6">
              <ListControls
                name={`change ${i + 1}`}
                index={i}
                count={items.length}
                onMove={(by) => {
                  keyOps.move(i, by)
                  onChange(moveItem(items, i, by))
                }}
                onDuplicate={() => {
                  keyOps.insert(i + 1)
                  onChange(insertAt(items, i + 1, clone(raw)))
                }}
                onRemove={() => {
                  keyOps.remove(i)
                  onChange(removeAt(items, i))
                }}
              />
            </div>
          </div>
        )
      })}
      <AddButton onClick={() => onChange([...items, { set: `$${roles[0] ?? 'actor'}.mood`, to: 'happy' }])}>Add a change</AddButton>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Fallbacks                                                                   */
/* -------------------------------------------------------------------------- */

/** `fallback`: try another action instead, with who plays which of its parts. */
export function FallbacksEditor({ items, onChange }: { items: unknown[]; onChange(next: unknown[]): void }) {
  const { roles, selfId, actions: known } = useEditor()
  const list = useQuery({ ...actionsQuery({}), enabled: known.length === 0 })
  const actions = known.length > 0 ? known : (list.data ?? []).map((a) => ({ id: a.id, label: a.label, roles: Object.keys(a.roles ?? {}) }))
  const options: Option[] = actions.filter((a) => a.id !== selfId).map((a) => ({ value: a.id, label: `${a.label} (${a.id})` }))
  const [keys, keyOps] = useListKeys(items.length)
  return (
    <div className="flex flex-col gap-3">
      {items.length === 0 ? <p className="text-sm text-ink-2">Nothing: the story just skips it.</p> : null}
      {items.map((raw, i) => {
        const fb = asObj(raw)
        const target = actions.find((a) => a.id === fb.action)
        const map = asObj(fb.map)
        const set = (next: Obj): void => onChange(items.map((x, j) => (j === i ? next : x)))
        return (
          <ItemCard
            key={keys[i]}
            path={`fallback.${i}`}
            title={`Option ${i + 1}`}
            controls={
              <ListControls
                name={`option ${i + 1}`}
                index={i}
                count={items.length}
                onMove={(by) => {
                  keyOps.move(i, by)
                  onChange(moveItem(items, i, by))
                }}
                onRemove={() => {
                  keyOps.remove(i)
                  onChange(removeAt(items, i))
                }}
              />
            }
          >
            <EditField path={`fallback.${i}.action`} label="Try this action instead">
              {(ctrl) => <SelectInput ctrl={ctrl} value={typeof fb.action === 'string' ? fb.action : undefined} options={options} onChange={(v) => set({ ...fb, action: v ?? '', map: {} })} />}
            </EditField>
            {target && target.roles.length > 0 ? (
              <div className="flex flex-col gap-2" data-field-path={`fallback.${i}.map`}>
                <p className="text-sm font-medium text-ink">Who plays its parts</p>
                <IssueLines path={`fallback.${i}.map`} deep />
                <div className="grid gap-2 md:grid-cols-2">
                  {target.roles.map((theirs) => (
                    <MapRow key={theirs} path={`fallback.${i}.map.${theirs}`} theirs={theirs} value={typeof map[theirs] === 'string' ? (map[theirs] as string) : ''} ours={roles} onChange={(v) => set({ ...fb, map: setOrDelete(map, [theirs], v) })} />
                  ))}
                </div>
              </div>
            ) : null}
          </ItemCard>
        )
      })}
      <AddButton onClick={() => onChange([...items, { action: '', map: {} }])}>Add something to try instead</AddButton>
    </div>
  )
}

function MapRow({ path, theirs, value, ours, onChange }: { path: string; theirs: string; value: string; ours: string[]; onChange(v: string | undefined): void }) {
  const id = useId()
  return (
    <div className="flex flex-col gap-1" data-field-path={path}>
      <label htmlFor={id} className="text-xs text-ink-2">
        Its {words(theirs)}
      </label>
      <select id={id} className={SELECT_CLASS} value={value} onChange={(e) => onChange(e.target.value || undefined)}>
        <option value="">{ours.includes(theirs) ? `The ${words(theirs)} (same name)` : 'Choose for me'}</option>
        {ours.map((r) => (
          <option key={r} value={`$${r}`}>
            {describeRef(`$${r}`)}
          </option>
        ))}
      </select>
    </div>
  )
}
